import React, { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router'

import { orderDueAmountStrings } from '../helpers/order-details'

import { Loading } from '../../shared/components'
import DateFormat from '../../shared/helpers/date-format'
import '../../../stylesheets/utility/flash-error.css'
import OrderExchangeView from '../components/order-exchange-view'

import { getOrderHistoryObject } from '../helpers/order-details'
import { useGetOrderHistoryIndex } from '../hooks/get-order-history-index.jsx'
import { useGetOrderDetails } from '../hooks/use-get-order-details.jsx'
import OrderItemsDisplay from '../components/order-items-display'
import { formatValueAsCurrency } from '../../shared/helpers/data-display-format'

import { getTotalTicketCost } from '../helpers/exchange-data'

let { dashed } = DateFormat

const OrderShowView = () => {
  document.body.scrollTop = document.documentElement.scrollTop = 0
  let { slashed } = DateFormat
  let params = useParams()
  const { isLoading: historyIsLoading, data: allOrders } =
    useGetOrderHistoryIndex()
  const orderHistoryIndex = allOrders?.orders
  const isMember = allOrders?.isMember
  const { isLoading, isError, data, isSuccess } = useGetOrderDetails(params.id)
  const [isInExchangeMode, setIsInExchangeMode] = useState(false)
  const [exchangeEventDetails, setExchangeEventDetails] = useState(null)
  const [isCancelOrder, setIsCancelOrder] = useState(false)
  const [showExchangeText, setShowExchangeText] = useState(false)
  const [orderObject, setOrderObject] = useState(null)
  const [orderPerformances, setOrderPerformances] = useState(null)

  useEffect(() => {
    // "data" is the result of the getOrderDetails call.  "orderHistoryIndex" is the result of the history custom proc.
    // Unless we have both pieces of info. return.
    if (!data || !orderHistoryIndex) {
      return
    }

    // Find the first instance of an order object from the history index.
    let orderHistoryObjectFromIndex = getOrderHistoryObject(
      orderHistoryIndex,
      params.id
    )

    // If we find the object, setOrderObject in state.
    if (
      orderHistoryObjectFromIndex &&
      Object?.keys(orderHistoryObjectFromIndex).length > 0
    ) {
      setOrderObject(orderHistoryObjectFromIndex)
    }
  }, [data, orderHistoryIndex])

  /*
   * Once we have the order object in state, we can start building the products (ie: performances) section of the order.
   * orderObject doesn't get set until BOTH the order history and order details calls have data so it should be safe
   * to call this like this.
   * */
  useEffect(() => {
    if (orderObject) {
      buildOrderProducts()
    }
  }, [orderObject])

  const buildOrderProducts = () => {
    if (orderObject.odProducts.length > 0) {
      let orderProducts = orderObject.odProducts.map(product => {
        let orderItem = {
          ...product,
          performance_details: data.odPerformances[product.perfNo]
        }

        //
        // this is now only 1 performance object in data.odPerformances because the tickets array was condensed to
        // a property of the single object.
        //
        const keys = Object.keys(data.odPerformances || {})
        let isRequiredAddOn = false
        let isAddOn = false

        // For each product we are iterating over, check and see if it is
        // a required or prompt as add on...if it is, then don't show the exchange
        // button for the item.
        keys.forEach(key => {
          if (
            data.odPerformances[key].requiredAddOn.indexOf(
              parseInt(product.perfNo)
            ) > -1 ||
            data.odPerformances[key].promptAsAddOn.indexOf(
              parseInt(product.perfNo)
            ) > -1
          ) {
            isRequiredAddOn = true
            isAddOn = true
            return
          }
        })

        orderItem.isRequiredAddOn = isRequiredAddOn
        orderItem.isAddOn = isAddOn

        orderItem.totalCost = getTotalTicketCost(
          orderItem.performance_details.ticketInfo
        )

        // If the base event's total cost is 0, then this is cancellable.
        let isCancellable =
          data.odMosAllowsExchange == true &&
          orderObject.odIsFullyPaid == 'yes' &&
          !orderItem.isAddOn &&
          orderItem.totalCost == 0
        // The "Cancel" option should only appear for $0 items, but we need to take into account
        // required add ons.  For example, members can get free member tickets but pay for a 4D.  In
        // that case, they should not see the cancel button because it will also cancel their 4D.
        // The initial isCancellable value is set to true if the total cost of the performance is 0 and the item in question
        // is NOT an add on.
        if (isCancellable) {
          // Iterate through all of the required add ons associated with this performance.
          orderItem.performance_details.requiredAddOn.every(addOnId => {
            // If the cost of the tickets for any of the required add ons > 0, then the
            // entire performance is NOT cancellable, so we can break out of the loop
            if (
              getTotalTicketCost(data.odPerformances[addOnId].ticketInfo) > 0
            ) {
              isCancellable = false
              return false
            }
            return true
          })

          orderItem.performance_details.promptAsAddOn.every(addOnId => {
            // If the cost of the tickets for any of the required add ons > 0, then the
            // entire performance is NOT cancellable, so we can break out of the loop
            if (
              getTotalTicketCost(data.odPerformances[addOnId].ticketInfo) > 0
            ) {
              isCancellable = false
              return false
            }
            return true
          })
        }

        orderItem.isCancellable = isCancellable

        let exchangeable =
          data.odMosAllowsExchange == true &&
          orderObject.odIsFullyPaid == 'yes' &&
          product.isExchangeable &&
          !isAddOn

        orderItem.isExchangeable = exchangeable

        // If this item is exchangeable and we have not already set
        // showExchangeText to true, then set it to false.
        if (exchangeable && showExchangeText == false) {
          setShowExchangeText(true)
        }

        return orderItem
      })

      setOrderPerformances(orderProducts)
    }
  }

  const buildExchangeDetailsItem = (
    orderObjectPerformance,
    orderDetailsPerformance
  ) => {
    let exchangeItemDetails = {
      exchangeData: {
        ...orderDetailsPerformance,
        zoneId: orderObjectPerformance.zoneNo,
        zoneDisplayName: orderObjectPerformance.zoneDisplayName,
        addOnNativeEventId:
          orderObjectPerformance.eventDetails.addOnNativeEventId,
        nativeEventId: orderObjectPerformance.eventDetails.nativeEventId
      },
      tickets: orderDetailsPerformance.ticketInfo
    }
    return exchangeItemDetails
  }

  const onClickExchange = async (e, eventId, perf_number, cancel = false) => {
    e.preventDefault()
    const perf = orderObject.odProducts.find(p => p.perfNo == perf_number)

    let exchangeDetails = buildExchangeDetailsItem(
      perf,
      data.odPerformances[perf_number]
    )

    exchangeDetails.exchangeFee = data.odExchangeFee
    exchangeDetails.itemsAutomaticallyIncludedInExchange = []
    exchangeDetails.itemsToPromptForExchange = []

    data.odPerformances[perf_number].requiredAddOn.forEach(perf_id => {
      if (
        orderObject.odProducts.find(p => p.perfNo == perf_id).isExchangeable
      ) {
        exchangeDetails.itemsAutomaticallyIncludedInExchange.push(
          buildExchangeDetailsItem(
            orderObject.odProducts.find(p => p.perfNo == perf_id),
            data.odPerformances[perf_id]
          )
        )
      }
    })

    data.odPerformances[perf_number].promptAsAddOn.forEach(perf_id => {
      if (
        orderObject.odProducts.find(p => p.perfNo == perf_id).isExchangeable
      ) {
        exchangeDetails.itemsToPromptForExchange.push(
          buildExchangeDetailsItem(
            orderObject.odProducts.find(p => p.perfNo == perf_id),
            data.odPerformances[perf_id]
          )
        )
      }
    })

    setIsCancelOrder(cancel)
    setIsInExchangeMode(true)
    setExchangeEventDetails(exchangeDetails)
  }
  const setPayments = () => {
    if (data) {
      return data.odPaymentHistory.map((payment, i) => (
        <li key={i}>
          {slashed(payment.date)}, {formatValueAsCurrency(payment.amount)} paid
          using {payment.cardName}
        </li>
      ))
    }
  }

  const getTotalContributions = contributions => {
    let contributionTotal = 0
    if (contributions.length > 0) {
      contributions.forEach(contribution => {
        contributionTotal += parseFloat(contribution.contributionAmount)
      })
    }
    return formatValueAsCurrency(contributionTotal) // contributionTotal.toFixed(2).replace(/\.00$/, '')
  }

  const setContributions = () => {
    if (orderObject.odContributions.length > 0) {
      return (
        <div>
          <p
            className="pb-0 mb-0 mt-40"
            data-testid={'order-details-contribution'}
          >
            <strong>Contributions</strong>
            <br />
            {getTotalContributions(orderObject.odContributions)}
          </p>
        </div>
      )
    }
  }

  const setMembership = () => {
    if (orderObject.odMembershipLevel.length > 0) {
      return orderObject.odMembershipLevel.map(membership => (
        <div key={membership.membershipLevel}>
          <p
            className="pb-0 mb-0 mt-40"
            data-testid={'order-detail-membership-level'}
          >
            <strong>{membership.membershipLevel} Membership</strong>
            <br />
            Purchased on {slashed(orderObject.odOrderDate)}
          </p>
        </div>
      ))
    }
  }

  const setGiftMembership = () => {
    if (orderObject.odGiftMembershipLevel.length > 0) {
      return orderObject.odGiftMembershipLevel.map(membership => (
        <div key={membership.giftMembershipLevel}>
          <p
            className="pb-0 mb-0 mt-40"
            data-testid={'order-detail-gift-membership-level'}
          >
            <strong>{membership.giftMembershipLevel} Membership</strong>
            <br />
            Purchased on {slashed(orderObject.odOrderDate)}
          </p>
        </div>
      ))
    }
  }

  const setAdoptions = () => {
    if (orderObject.odAdoptions.length > 0) {
      return orderObject.odAdoptions.map((adoption, i) => (
        <div key={i}>
          <p
            className="pb-0 mb-0 mt-40"
            data-testid={'order-detail-adoption'}
          >
            <strong>
              {adoption.animalName !== null ? adoption.animalName : 'Animal'}{' '}
              Adoption
            </strong>
            <br />
            {formatValueAsCurrency(adoption.contributionAmount)}
          </p>
        </div>
      ))
    }
  }

  const setGiftCertificates = () => {
    if (data && data.odGiftCertificates.length > 0) {
      return data.odGiftCertificates.map((giftCertificate, i) => (
        <div key={i}>
          <p
            className="pb-0 mb-0 mt-40"
            data-testid={'order-detail-gift-certificate'}
          >
            <strong>Gift Certificate</strong>
            <br />
            {formatValueAsCurrency(giftCertificate.amount)}
          </p>
        </div>
      ))
    }
  }

  const setPledgeCopy = params => {
    if (data.odBalanceAmount == 0) {
      return (
        <>
          <p
            className="font-bold"
            data-testid={'order-detail-pledge-paid'}
          >
            Thank you for your contribution! Your pledge payment has been
            completed.
          </p>
          <p className="italic">
            Need a new tax receipt? Reach out by using the subject line "Attn:
            Donor Services" at the email address below.
          </p>
        </>
      )
    } else if (data.odBalanceAmount > 0) {
      return (
        <>
          <p
            className="font-bold"
            data-testid={'order-detail-pledge-due'}
          >
            Thank you for your support. Your pledge commitment is due on{' '}
            {slashed(orderObject.odPledgeDue)}.
          </p>
          <p className="italic">
            Need to adjust your payment schedule or a new tax receipt? Reach out
            by using the subject line "Attn: Donor Services" at the email
            address below.
          </p>
        </>
      )
    }
  }

  const cancelExchangeMode = () => {
    setIsInExchangeMode(false)
    setExchangeEventDetails(null)
  }

  const setRelatedOrders = () => {
    return data.odRelatedOrders.map((ro, i) => (
      <li key={i}>{buildRelatedOrderInfo(ro)}</li>
    ))
  }

  const buildRelatedOrderInfo = relatedOrder => {
    if (relatedOrder.originalOrderNo == params.id) {
      if (relatedOrder.action == 'E') {
        return (
          <>
            Some of the item(s) from this order were exchanged in order{' '}
            {buildLink(relatedOrder.newOrderNo)} on{' '}
            {slashed(relatedOrder.insertDate)}.
          </>
        )
      } else {
        return (
          <>
            Some of the item(s) in this order were cancelled on{' '}
            {slashed(relatedOrder.insertDate)}.
          </>
        )
      }
    } else {
      if (relatedOrder.action == 'E') {
        return (
          <>
            This order used a credit from items that were exchanged in order{' '}
            {buildLink(relatedOrder.originalOrderNo)} on{' '}
            {slashed(relatedOrder.insertDate)}.
          </>
        )
      } else {
        return (
          <>
            This order was created when order{' '}
            {buildLink(relatedOrder.originalOrderNo)} was cancelled on{' '}
            {slashed(relatedOrder.insertDate)}.
          </>
        )
      }
    }
  }

  const buildLink = orderNo => {
    return <a href={`/order_history/${orderNo}`}>{orderNo}</a>
  }

  return (
    <div className="pb-60">
      <div className="w-full">
        {!isInExchangeMode && (
          <Link to={`/order_history`}>
            <button className="text-20 text-depths pb-8 m-top-0 mb-8">
              {'< Back to order listing'}
            </button>
          </Link>
        )}
        {isInExchangeMode && (
          <button
            className="text-20 text-depths pb-8 m-top-0 mb-8"
            onClick={cancelExchangeMode}
          >
            {'< Back to order details'}
          </button>
        )}
      </div>

      {(isLoading || historyIsLoading) && <Loading />}

      {!isLoading && !historyIsLoading && (isError || !orderObject) && (
        <p className="font-bold">
          The details of this order cannot be loaded. If you need further
          assistance, please contact customer service via phone at 312-939-2438
          or email at{' '}
          <a href="mailto:contactus@sheddaquarium.org">
            contactus@sheddaquarium.org
          </a>
        </p>
      )}

      {isSuccess &&
        !historyIsLoading &&
        !isError &&
        orderObject &&
        !isInExchangeMode && (
          <div>
            <div
              key={data}
              className="bordered-content"
            >
              <h3 className="order-history-header">Order #{params.id}</h3>

              {orderObject.odIsPledge !== 'Y' && (
                <p
                  className={
                    data.odBalanceAmount > 0
                      ? 'font-bold mb-0'
                      : 'font-light mb-0'
                  }
                  data-testid={'order-due-amount-string'}
                >
                  {orderDueAmountStrings(
                    data.odBalanceAmount,
                    orderObject.odPaidInFullDate,
                    orderObject.odProducts
                  )}
                </p>
              )}
              {orderObject.odIsPledge === 'Y' && setPledgeCopy()}
              <div>
                {data && (
                  <ul
                    className="bullet-list order-history-bullet-list"
                    data-testid={'order-payments-list'}
                  >
                    {setPayments()}
                  </ul>
                )}

                <p
                  className="mt-0"
                  data-testid={'order-balance-remaining'}
                >
                  Balance Remaining:{' '}
                  {formatValueAsCurrency(data.odBalanceAmount)}
                </p>

                {data && data.odRelatedOrders && (
                  <ul
                    className="bullet-list order-history-bullet-list"
                    data-testid={'order-related-orders'}
                  >
                    {setRelatedOrders()}
                  </ul>
                )}
                {orderObject.odIsFullyPaid &&
                  orderObject.odProducts.length == 0 &&
                  data.odRelatedOrders.length == 0 &&
                  data.odPaymentHistory.length == 0 && (
                    <p data-testid={'contained-cancelled-items'}>
                      This order contained items that were cancelled.
                    </p>
                  )}
                {showExchangeText && (
                  <p data-testid={'exchange-text'}>
                    {`Have your plans changed? You may exchange your tickets for just ${formatValueAsCurrency(
                      data.odExchangeFee
                    )} per order (no fee for members).`}
                  </p>
                )}
              </div>

              {orderPerformances && (
                <OrderItemsDisplay
                  orderItems={orderPerformances}
                  handleOnClickExchange={onClickExchange}
                ></OrderItemsDisplay>
              )}
              {setMembership()}
              {setGiftMembership()}
              {setGiftCertificates()}
              {setAdoptions()}
              {setContributions()}
              {/* if coming straight to an order details page without going through the order history screen,
            the param is coming from the url. This is why it is labeled 'param'. See line 17 for variable declaration*/}
              {/*  {setProducts(params)}
              {setMembership(params)}
              {setGiftMembership(params)}
              {setGiftCertificates(params)}
              {setAdoptions(params)}
              {setContributions(params)}*/}
            </div>

            <p className="xs-paragraph">
              If you have any questions or concerns about this order, please
              contact
              <br />
              <a href="mailto:contactus@sheddaquarium.org">
                contactus@sheddaquarium.org
              </a>
            </p>
          </div>
        )}
      {isInExchangeMode && (
        <div key={data}>
          <OrderExchangeView
            order={orderObject}
            isMember={isMember}
            // itemsToExchange={itemsToExchange}
            exchangeItem={exchangeEventDetails}
            isCancelRequest={isCancelOrder}
          />
        </div>
      )}
    </div>
  )
}

export default OrderShowView
